import { format } from 'date-fns'
import { parseDateLocal } from '~/util/date'
import type { WalkTimelineRow } from '~/api/endpoints/walks'
import { UNIT_TO_BUCKET_FN, type GranularityUnit } from './dataPipeline'
import { seriesKeyForRow, hasMultipleValueTypes } from './TimelineChart'

/** Coarsen ratio rows to `unit` (and optionally cumulate over time) CORRECTLY — by summing the
 *  numerator and denominator components, then dividing. NEVER sums the ratio value (invalid). Returns
 *  value-rows (value = Σnum/Σdenom; components dropped) for the unchanged pivot. A bucket whose
 *  denominator sums to 0 → value null (a gap). Grouped per (dims, value_type) series. */
export function aggregateRatioRows(
  rows: WalkTimelineRow[],
  unit: GranularityUnit,
  runningTotal: boolean,
): WalkTimelineRow[] {
  const bucketFn = UNIT_TO_BUCKET_FN[unit]
  type Acc = { num: number; denom: number; rep: WalkTimelineRow }
  const bySeries = new Map<string, Map<string, Acc>>()
  for (const r of rows) {
    // Internal grouping-only key; a DIFFERENT scheme from the pivot's seriesKeyForRow. Both are
    // injective on (dims, value_type) so they partition identically — but only
    // RatioBucketComponents.perSeries (which uses seriesKeyForRow) matches the pivot's key space.
    const sKey = JSON.stringify(r.dims ?? null) + ' ' + (r.value_type ?? '')
    const bucket = format(bucketFn(parseDateLocal(r.time)), 'yyyy-MM-dd')
    let buckets = bySeries.get(sKey)
    if (!buckets) {
      buckets = new Map()
      bySeries.set(sKey, buckets)
    }
    // no-silent-fallbacks: a walk_ratio row ALWAYS carries a numerator (the num CTE is the LEFT-JOIN
    // base). An undefined numerator means isRatio was wired to a non-ratio walk (or a pre-mig row) →
    // fail loudly rather than silently summing 0s. The denominator MAY be undefined (a bucket with no
    // denominator/spend) → treat as 0 → value null (a legitimate gap).
    if (r.numerator === undefined) {
      throw new Error(
        `aggregateRatioRows: ratio row has no numerator (${r.kpi_type} @ ${r.time}) — isRatio wired to a non-ratio walk?`,
      )
    }
    const cur = buckets.get(bucket) ?? { num: 0, denom: 0, rep: r }
    cur.num += r.numerator
    cur.denom += r.denominator ?? 0
    buckets.set(bucket, cur)
  }
  const out: WalkTimelineRow[] = []
  for (const buckets of bySeries.values()) {
    const sorted = [...buckets.entries()].sort((a, b) => a[0].localeCompare(b[0]))
    let runNum = 0
    let runDenom = 0
    for (const [bucket, acc] of sorted) {
      let num = acc.num
      let denom = acc.denom
      if (runningTotal) {
        runNum += acc.num
        runDenom += acc.denom
        num = runNum
        denom = runDenom
      }
      out.push({
        kpi_type: acc.rep.kpi_type,
        time: bucket,
        value_type: acc.rep.value_type,
        dims: acc.rep.dims,
        value: denom === 0 ? null : num / denom,
      })
    }
  }
  return out
}

// ---- components sidecar ----

export interface RatioBucketComponents {
  /** num per series key (key derivation = pivot's, via seriesKeyForRow).
   *  Deliberately num-only: cross-series ratio math must use bucketDenom (denom-once);
   *  a per-series denom field would invite double-counting. */
  perSeries: Map<string, { num: number }>
  /** The bucket's total denominator, summed once per distinct (raw-time, denom-value) pair.
   *  mig-0080: a both-sides-grouped ratio (e.g. RoI by Medium Level 1) carries a DIFFERENT
   *  denominator per series (each medium's own spend) → those distinct per-slice denominators
   *  must be summed. A num-only-grouped ratio (e.g. RoI by KPI Level 1) broadcasts the same
   *  denominator value across all series at a time → deduping by value collapses it to once.
   *  Distinct denom-slices are identified by their exact float value; exact ties between
   *  different slices at the same instant are effectively impossible for summed-EUR spend. */
  bucketDenom: number
}
export type RatioComponentsByBucket = Map<string, RatioBucketComponents>

/** aggregateRatioRows + a per-bucket components sidecar for panel aggregates.
 *  Components are ALWAYS non-cumulative — panel Σ needs raw per-bucket parts even
 *  when the chart view is running-total. Value rows respect `runningTotal` as before. */
export function aggregateRatioRowsWithComponents(
  rows: WalkTimelineRow[],
  unit: GranularityUnit,
  runningTotal: boolean,
  // FU-h: override for the perSeries (seriesKeyForRow) key space ONLY — the internal
  // grouping key in aggregateRatioRows is a different, injective scheme and stays untouched.
  showVtOverride?: boolean,
): { rows: WalkTimelineRow[]; components: RatioComponentsByBucket } {
  const out = aggregateRatioRows(rows, unit, runningTotal)
  const bucketFn = UNIT_TO_BUCKET_FN[unit]
  const showVt = showVtOverride ?? hasMultipleValueTypes(rows)
  const components: RatioComponentsByBucket = new Map()
  // denomByTime: bucket → ((raw-time + denom-value composite key) → denom-value).
  // mig-0080: a both-sides-grouped ratio carries a DIFFERENT denominator per series (each
  // medium's own spend). We must sum each distinct per-slice denominator once. A num-only
  // grouped ratio broadcasts the same denominator value across all series at one raw time —
  // deduping by (time, value) naturally collapses that to one count. The composite key is
  // r.time + '\0' + String(r.denominator ?? 0); exact float ties between different real
  // spend-slices at the same instant are effectively impossible for summed-EUR values.
  const denomByTime = new Map<string, Map<string, number>>()
  for (const r of rows) {
    // numerator guard: the aggregateRatioRows call above throws on the first bad row — no repeat check.
    const bucket = format(bucketFn(parseDateLocal(r.time)), 'yyyy-MM-dd')
    const sKey = seriesKeyForRow(r, showVt)
    let comp = components.get(bucket)
    if (!comp) {
      comp = { perSeries: new Map(), bucketDenom: 0 }
      components.set(bucket, comp)
    }
    const s = comp.perSeries.get(sKey) ?? { num: 0 }
    s.num += r.numerator! // safe: aggregateRatioRows above threw on any undefined numerator
    comp.perSeries.set(sKey, s)
    let dbt = denomByTime.get(bucket)
    if (!dbt) {
      dbt = new Map()
      denomByTime.set(bucket, dbt)
    }
    // Composite key = raw-time + denom-value. Records each distinct (time, denom-slice) once:
    // - both-sides grouping: different denom per series → different keys → all summed (the fix).
    // - num-only / broadcast grouping: same denom value → same key → counted once (no change).
    const denomKey = r.time + '\0' + String(r.denominator ?? 0)
    if (!dbt.has(denomKey)) dbt.set(denomKey, r.denominator ?? 0)
  }
  for (const [bucket, dbt] of denomByTime) {
    components.get(bucket)!.bucketDenom = [...dbt.values()].reduce((a, b) => a + b, 0)
  }
  return { rows: out, components }
}

/** Panel Σ across ALL series: Σnums / Σ(bucketDenom-once). null when the panel's denominator is 0. */
export function ratioPanelSum(
  bucketTimes: string[],
  components: RatioComponentsByBucket,
): number | null {
  let num = 0
  let denom = 0
  for (const t of bucketTimes) {
    const c = components.get(t)
    if (!c) continue
    for (const s of c.perSeries.values()) num += s.num
    denom += c.bucketDenom
  }
  return denom === 0 ? null : num / denom
}

/** Per-series panel Σ: Σ(num_s) / Σ(panel-wide bucketDenom). The denominator spans all
 *  panel buckets even where the series has no rows (window-ratio semantics, spec §2). */
export function ratioSeriesPanelSums(
  bucketTimes: string[],
  components: RatioComponentsByBucket,
): Map<string, number | null> {
  let denom = 0
  const numBySeries = new Map<string, number>()
  for (const t of bucketTimes) {
    const c = components.get(t)
    if (!c) continue
    denom += c.bucketDenom
    for (const [k, s] of c.perSeries) numBySeries.set(k, (numBySeries.get(k) ?? 0) + s.num)
  }
  const out = new Map<string, number | null>()
  for (const [k, n] of numBySeries) out.set(k, denom === 0 ? null : n / denom)
  return out
}
